import React, { useEffect, useState } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  TextField,
  Typography,
} from '@mui/material'
import adminFieldService from '../../../services/admin/field'
import { toast } from 'react-toastify'

const ImportPageDialog = ({
  open,
  setOpen,
  ftData,
  pageId = null,
  importedFieldList,
  activityTemplateId,
  onDeriveSuccess,
}) => {
  const { user } = useAuthContext()
  const [fields, setFields] = useState([])
  const [allActivities, setAllActivities] = useState([])
  const [processes, setProcesses] = useState([])
  const [selectedProcess, setSelectProcess] = useState(null)
  const [allPages, setAllPages] = useState([])
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [derivingData, setDerivingData] = useState([])
  const [pivotFields, setPivotFields] = useState([])
  const [selectedPivotField, setSelectedPivotField] = useState(null)
  const [importableFields, setImportableFields] = useState([])

  useEffect(() => {
    const pivots = ftData.filter((it) => it.is_pivot_field == true)
    setPivotFields(pivots)
  }, [ftData])

  useEffect(() => {
    // filter all importable fields from all  list
    const importable = importedFieldList.filter((it) =>
      selectedPivotField?.import_from_process_template_id
        ?.split(',')
        ?.map((it) => +it)
        ?.includes(it.process_template_id)
    )
    setImportableFields(() => importable)
  }, [selectedPivotField])

  useEffect(() => {
    ;(async () => {
      if (open) {
        // get all processes
        const processes = importableFields.reduce((acc, cur) => {
          const process = acc?.find((it) => it.id == cur.process_template_id)
          if (!process) {
            return [
              ...acc,
              {
                id: cur.process_template_id,
                process_template_name: cur.process_template_name,
              },
            ]
          }
          return acc
        }, [])
        setProcesses(processes)
      }
    })()
  }, [open, importableFields])

  useEffect(() => {
    ;(async () => {
      if (open) {
        // get all activities
        const activities = importableFields
          ?.filter((it) => it.process_template_id == selectedProcess?.id)
          ?.reduce((acc, cur) => {
            const activity = acc?.find((it) => it.id == cur.activity_template_id)
            if (!activity) {
              return [
                ...acc,
                {
                  id: cur.activity_template_id,
                  activity_template_name: cur.activity_template_name,
                  sequence: cur.activity_sequence,
                },
              ]
            }
            return acc
          }, [])
        setAllActivities(activities.filter((it) => it.id != activityTemplateId))

        // get all pages
        const pages = importableFields
          ?.filter((it) => it.process_template_id == selectedProcess?.id)
          ?.reduce((acc, cur) => {
            const page = acc?.find((it) => it.id == cur.page_template_id)
            if (!page) {
              return [
                ...acc,
                {
                  id: cur.page_template_id,
                  page_template_name: cur.page_template_name,
                  sequence: cur.page_sequence,
                  activity_template_id: cur.activity_template_id,
                },
              ]
            }
            return acc
          }, [])
        setAllPages(pages)
      }
    })()
  }, [open, importableFields, selectedProcess])

  const derivePageWithFields = async () => {
    try {
      // call derive api
      await adminFieldService.importFieldsInBulk(user, activityTemplateId, {
        data: derivingData,
        page_id: pageId,
        pivot_field_id: selectedPivotField.id,
      })
      setOpen(false)
      onDeriveSuccess()
      toast.success('Fields are imported successfully')
    } catch (error) {
      toast.error('Something went wrong!')
    }
  }

  return (
    <DialogWrapper open={open} onClose={() => setOpen(false)} heading={'Import Fields'}>
      <Box>
        {/* select pivot field */}
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <Autocomplete
            id="Select Pivot Field"
            options={pivotFields}
            size="small"
            getOptionLabel={(item) => item.field_template_name}
            value={
              selectedPivotField?.id ? ftData.find((it) => it.id == selectedPivotField?.id) : null
            }
            onChange={(_, v) => setSelectedPivotField(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Select Pivot Field "
                color="primary"
              />
            )}
          />
        </FormControl>
        {/* select process */}
        <FormControl
          variant="outlined"
          fullWidth
          size="small"
          color="primary"
          style={{ minWidth: 300, marginTop: 16 }}
        >
          <Autocomplete
            id="select-derived-field"
            options={processes}
            size="small"
            getOptionLabel={(item) => item?.process_template_name}
            value={selectedProcess}
            onChange={(_, v) => setSelectProcess(v)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Select Process" color="primary" />
            )}
          />
        </FormControl>
        {/* select activity */}
        <FormControl
          variant="outlined"
          fullWidth
          size="small"
          color="primary"
          style={{ minWidth: 300, marginTop: 16 }}
        >
          <Autocomplete
            id="select-derived-field"
            options={allActivities}
            size="small"
            getOptionLabel={(item) => item?.activity_template_name}
            value={selectedActivity}
            onChange={(_, v) => setSelectedActivity(v)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Select Activity" color="primary" />
            )}
          />
        </FormControl>

        {/* select page */}
        {allPages
          .filter((it) => it.activity_template_id == selectedActivity?.id)
          .map((page) => {
            return (
              <Box>
                <Checkbox
                  color="primary"
                  checked={
                    derivingData.find((it) => it.page_id == page.id) != undefined && pageId == null
                  }
                  disabled={pageId != null}
                  onChange={() => {
                    if (derivingData.find((it) => it.page_id == page.id) != undefined) {
                      setDerivingData(derivingData.filter((it) => it.page_id != page.id))
                    } else {
                      setDerivingData([
                        ...derivingData,
                        {
                          page_id: page.id,
                          field_ids: fields
                            .filter((it) => it.page_template_id == page.id)
                            .map((it) => it.id),
                        },
                      ])
                    }
                  }}
                />
                <Typography variant="p">{page.page_template_name}</Typography>

                <Box>
                  {importedFieldList
                    .filter(
                      (it) =>
                        it.page_template_id == page.id && !it.is_subfield && !it.is_derived_field
                    )
                    .map((field) => {
                      const data = derivingData?.find((it) => it.page_id == page.id)
                      return (
                        <Box style={{ marginLeft: 24 }}>
                          <Checkbox
                            color="primary"
                            checked={data?.field_ids?.includes(field.id)}
                            onChange={() => {
                              if (data) {
                                if (data.field_ids?.includes(field.id)) {
                                  data.field_ids = data?.field_ids?.filter((it) => it != field.id)
                                  setDerivingData(
                                    derivingData.map((it) => (it.page_id == page.id ? data : it))
                                  )
                                } else {
                                  data.field_ids = [...data.field_ids, field.id]
                                  setDerivingData(
                                    derivingData.map((it) => (it.page_id == page.id ? data : it))
                                  )
                                }
                              } else {
                                setDerivingData([
                                  ...derivingData,
                                  { page_id: page.id, field_ids: [field.id] },
                                ])
                              }
                            }}
                          />
                          <Typography variant="p">{field.field_template_name}</Typography>
                        </Box>
                      )
                    })}
                </Box>
              </Box>
            )
          })}
        <Box
          style={{
            display: 'flex',
            columnGap: 16,
            width: '100%',
            justifyContent: 'flex-end',
            marginTop: 16,
          }}
        >
          <Button variant="contained" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={derivePageWithFields}>
            Save
          </Button>
        </Box>
      </Box>
    </DialogWrapper>
  )
}

export default ImportPageDialog
